'use client';

import {
  IconAlertCircle,
  IconAlertTriangle,
  IconCircleCheck,
  IconInfoCircle,
  IconX,
} from '@tabler/icons-react';
import { Toaster as Sonner, toast } from 'sonner';

import { cn } from '@/lib/utils';

type ToasterProps = React.ComponentProps<typeof Sonner>;

const toastIconClassName = 'size-6 shrink-0';

const toastVariants = {
  base: 'relative flex w-full items-start gap-1 rounded-xl border p-4 font-sans text-base font-medium leading-6',
  success:
    'border-border-success bg-background-surface-success-subtle text-text-success',
  error:
    'border-border-danger bg-background-surface-danger-subtle text-text-danger',
  warning:
    'border-border-warning bg-background-surface-warning-subtle text-text-warning',
  info: 'border-border-info bg-background-surface-info-subtle text-text-info',
  default: 'border-border bg-background text-foreground',
};

const Toaster = ({
  className,
  style,
  toastOptions,
  ...props
}: ToasterProps) => {
  return (
    <Sonner
      className={cn('toaster group', className)}
      closeButton
      expand
      icons={{
        success: (
          <IconCircleCheck className={toastIconClassName} stroke={1.5} />
        ),
        error: <IconAlertCircle className={toastIconClassName} stroke={1.5} />,
        warning: (
          <IconAlertTriangle className={toastIconClassName} stroke={1.5} />
        ),
        info: <IconInfoCircle className={toastIconClassName} stroke={1.5} />,
        close: <IconX className={toastIconClassName} stroke={1.5} />,
      }}
      style={{ '--width': '377px', pointerEvents: 'auto', ...style } as React.CSSProperties}
      theme='light'
      toastOptions={{
        unstyled: true,
        ...toastOptions,
        classNames: {
          toast: toastVariants.base,
          success: toastVariants.success,
          error: toastVariants.error,
          warning: toastVariants.warning,
          info: toastVariants.info,
          default: toastVariants.default,
          icon: 'shrink-0',
          content: 'flex min-w-0 flex-1 flex-col gap-2 pr-8',
          description: 'text-sm font-normal leading-[22px]',
          closeButton:
            'absolute top-[3px] right-[3px] flex min-h-12 min-w-12 cursor-pointer items-center justify-center bg-transparent text-current transition-opacity hover:opacity-70',
          ...toastOptions?.classNames,
        },
      }}
      {...props}
    />
  );
};

const preventDismissOnToast = (event: Event) => {
  if (
    event.target instanceof Element &&
    event.target.closest('[data-sonner-toaster]')
  ) {
    event.preventDefault();
  }
};

export { Toaster, toast, preventDismissOnToast };
